import { useEffect, useMemo, useState } from 'react';
import {
  IconAlertTriangle,
  IconCalendarEvent,
  IconCircleCheck,
  IconEdit,
  IconLock,
  IconPlus,
  IconScale,
  IconTrash,
  IconUserShield,
  IconUsersGroup,
} from '@tabler/icons-react';
import Btn from './ui/Btn';
import ActesProceduralPanel from './ActesProceduralPanel';
import DeadlinesGrid from './DeadlinesGrid';
import TimelineStepper from './TimelineStepper';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';
import {
  EVENT_META,
  PHASE_LABELS,
  PHASE_ORDER,
  ROLE_LABELS,
  deadlineLabel,
  eventLabel,
  filterEventsByRole,
  phaseLabel,
} from '../lib/procedureMeta';
import type {
  DetentionStatus,
  DossierRole,
  ProcedureBundle,
  ProcedureEvent,
  ProcedureEventType,
  ProcedurePhase,
  ProcedureState,
} from '../lib/types';

/**
 * Onglet « État de la procédure » (ADR-026).
 *
 * - Vue chronologique des phases procédurales BE (préliminaire → exécution).
 * - Toggle rôle dossier (suspect/PC) — filtre les événements pertinents
 *   et change les libellés.
 * - Toggle mode lecture client (langage déjuridicisé, sans acronymes).
 * - Sub-rail détention préventive (parallèle aux phases).
 * - Ajout/édition/suppression d'événements typés.
 * - Délais auto-dérivés affichés en badge (J-3 rouge, J-7 jaune).
 */

const DETENTION_LABELS: Record<DetentionStatus, { lawyer: string; client: string }> = {
  libre: { lawyer: 'Libre', client: 'Libre' },
  detenu: { lawyer: 'Détenu (détention préventive)', client: 'En détention' },
  libere_conditions: {
    lawyer: 'Libéré sous conditions',
    client: 'Libéré avec conditions à respecter',
  },
  bracelet: { lawyer: 'Surveillance électronique', client: 'Bracelet électronique' },
  internement: { lawyer: 'Internement', client: 'Internement' },
};

type Props = {
  dossierId: string;
};

export default function EtatProcedureTab({ dossierId }: Props) {
  const [bundle, setBundle] = useState<ProcedureBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = async () => {
    try {
      const data = await api.get<ProcedureBundle>(`/dossiers/${dossierId}/procedure`);
      setBundle(data);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId]);

  const state = bundle?.state ?? null;
  const events = bundle?.events ?? [];
  const mode: 'lawyer' | 'client' = state?.client_facing_mode ? 'client' : 'lawyer';

  const filteredEvents = useMemo(() => {
    if (!state) return [];
    return filterEventsByRole(events, state.dossier_role);
  }, [events, state]);

  const eventsByPhase = useMemo(() => {
    const map = new Map<ProcedurePhase, ProcedureEvent[]>();
    for (const phase of PHASE_ORDER) map.set(phase, []);
    for (const e of filteredEvents) {
      const phase = EVENT_META[e.event_type].phase;
      map.get(phase)!.push(e);
    }
    for (const [, arr] of map) arr.sort((a, b) => a.event_date.localeCompare(b.event_date));
    return map;
  }, [filteredEvents]);

  const patchState = async (patch: Partial<ProcedureState>) => {
    try {
      const data = await api.patch<ProcedureState>(`/dossiers/${dossierId}/procedure`, patch);
      setBundle((b) => (b ? { ...b, state: data } : b));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur de mise à jour');
    }
  };

  const deleteEvent = async (event: ProcedureEvent) => {
    if (!confirm(`Supprimer l'événement « ${eventLabel(event.event_type, mode, event.label)} » ?`))
      return;
    try {
      await api.delete(`/procedure-events/${event.id}`);
      void load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur suppression');
    }
  };

  // Task #6 — bascule is_checked pour les étapes futures cliquables.
  // Mise à jour optimiste de bundle puis PATCH.
  const toggleChecked = async (event: ProcedureEvent) => {
    const next = !event.is_checked;
    setBundle((b) =>
      b
        ? {
            ...b,
            events: b.events.map((e) => (e.id === event.id ? { ...e, is_checked: next } : e)),
          }
        : b
    );
    try {
      await api.patch<ProcedureEvent>(`/procedure-events/${event.id}`, {
        is_checked: next,
      });
    } catch (e) {
      // Rollback en cas d'erreur.
      setBundle((b) =>
        b
          ? {
              ...b,
              events: b.events.map((ev) =>
                ev.id === event.id ? { ...ev, is_checked: !next } : ev
              ),
            }
          : b
      );
      toast.error(e instanceof ApiError ? e.message : 'Erreur mise à jour');
    }
  };

  const [proposing, setProposing] = useState(false);
  const proposePhase = async () => {
    setProposing(true);
    try {
      const data = await api.post<ProcedureState>(`/dossiers/${dossierId}/procedure/propose`);
      setBundle((b) => (b ? { ...b, state: data } : b));
      toast.success('Proposition IA reçue — vérifie et valide');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur proposition IA');
    } finally {
      setProposing(false);
    }
  };

  const acceptAiPhase = () => {
    if (!state?.ai_proposed_phase) return;
    void patchState({ current_phase: state.ai_proposed_phase });
  };

  if (loading) {
    return (
      <div className="px-6 py-5 text-[13px] text-text-secondary">
        Chargement de l'état de la procédure…
      </div>
    );
  }
  if (error || !state) {
    return (
      <div className="px-6 py-5 text-[13px] text-cat-def">
        {error ?? "Pas d'état procédural disponible"}
      </div>
    );
  }

  return (
    <div className="space-y-5 px-6 py-5">
      <header className="space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-serif text-[20px] font-medium text-text-primary">
              État de la procédure
            </h1>
            <p className="mt-1 text-[13px] text-text-secondary">
              {mode === 'client'
                ? 'Vue chronologique simplifiée — où en est votre dossier.'
                : 'Phase courante, chronologie typée, délais légaux dérivés.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <RoleToggle
              role={state.dossier_role}
              onChange={(role) => void patchState({ dossier_role: role })}
            />
            <button
              type="button"
              onClick={() => void patchState({ client_facing_mode: !state.client_facing_mode })}
              className={`rounded-pill border px-3 py-1 text-[12px] ${
                state.client_facing_mode
                  ? 'border-accent bg-accent-light text-accent-text'
                  : 'border-rule bg-paper-elevated text-text-secondary'
              }`}
              title="Bascule entre langage avocat et langage compréhensible client"
            >
              {state.client_facing_mode ? '👁 Mode client' : '👁 Mode avocat'}
            </button>
          </div>
        </div>

        <TimelineStepper
          currentPhase={state.current_phase}
          currentSubState={state.sub_state ?? null}
          visitedPhases={
            new Set(
              Array.from(eventsByPhase.entries())
                .filter(([, evs]) => evs.length > 0)
                .map(([phase]) => phase)
            )
          }
          varianteAssises={state.variante_assises ?? false}
          mode={mode}
          onPick={(phase) => void patchState({ current_phase: phase })}
        />

        <PhaseSummary
          state={state}
          mode={mode}
          onChange={(phase) => void patchState({ current_phase: phase })}
          onProposeAi={proposePhase}
          onAcceptAi={acceptAiPhase}
          proposing={proposing}
        />

        <DetentionRail state={state} mode={mode} onChange={(patch) => void patchState(patch)} />

        <DeadlinesGrid dossierId={dossierId} />
      </header>

      <section className="relative">
        <div className="absolute left-[14px] top-2 bottom-2 w-px bg-rule" aria-hidden="true" />

        <div className="space-y-6">
          {PHASE_ORDER.map((phase) => {
            const phaseEvents = eventsByPhase.get(phase) ?? [];
            if (phaseEvents.length === 0 && phase !== state.current_phase) return null;
            return (
              <PhaseSection
                key={phase}
                phase={phase}
                events={phaseEvents}
                mode={mode}
                isCurrent={phase === state.current_phase}
                onEdit={setEditingId}
                onDelete={(ev) => void deleteEvent(ev)}
                onToggleChecked={(ev) => void toggleChecked(ev)}
              />
            );
          })}
        </div>
      </section>

      <ActesProceduralPanel dossierId={dossierId} currentPhase={state.current_phase} />

      <div className="sticky bottom-4 flex justify-end">
        <Btn type="button" variant="primary" onClick={() => setAddOpen(true)}>
          <IconPlus size={16} stroke={1.6} />
          <span>Ajouter un événement</span>
        </Btn>
      </div>

      {addOpen && (
        <EventModal
          dossierId={dossierId}
          mode={mode}
          role={state.dossier_role}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            void load();
          }}
        />
      )}

      {editingId && (
        <EventModal
          dossierId={dossierId}
          mode={mode}
          role={state.dossier_role}
          existing={events.find((e) => e.id === editingId) ?? null}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            setEditingId(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Role toggle
// ---------------------------------------------------------------------------

function RoleToggle({
  role,
  onChange,
}: {
  role: DossierRole;
  onChange: (role: DossierRole) => void;
}) {
  return (
    <div className="inline-flex rounded-pill border border-rule bg-paper-elevated p-0.5 text-[12px]">
      {(['suspect_prevenu', 'partie_civile'] as DossierRole[]).map((r) => {
        const active = r === role;
        const Icon = r === 'suspect_prevenu' ? IconUserShield : IconUsersGroup;
        return (
          <button
            key={r}
            type="button"
            onClick={() => onChange(r)}
            className={`flex items-center gap-1.5 rounded-pill px-2.5 py-1 transition ${
              active
                ? 'bg-accent text-paper-elevated'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <Icon size={14} stroke={1.6} />
            {ROLE_LABELS[r].short}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase summary (current phase + selector)
// ---------------------------------------------------------------------------

function PhaseSummary({
  state,
  mode,
  onChange,
  onProposeAi,
  onAcceptAi,
  proposing,
}: {
  state: ProcedureState;
  mode: 'lawyer' | 'client';
  onChange: (phase: ProcedurePhase) => void;
  onProposeAi: () => void;
  onAcceptAi: () => void;
  proposing: boolean;
}) {
  return (
    <div className="rounded-md border border-rule bg-paper-subtle p-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-text-tertiary">Phase courante</p>
          <p className="mt-0.5 font-medium text-[14px] text-text-primary">
            {phaseLabel(state.current_phase, mode)}
          </p>
          {state.juridiction && (
            <p className="mt-0.5 text-[12px] text-text-secondary">{state.juridiction}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={state.current_phase}
            onChange={(e) => onChange(e.target.value as ProcedurePhase)}
            className="rounded-md border border-rule-strong bg-paper-elevated px-2 py-1 text-[12.5px]"
          >
            {PHASE_ORDER.map((p) => (
              <option key={p} value={p}>
                {PHASE_LABELS[p][mode]}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={onProposeAi}
            disabled={proposing}
            title="Sonnet propose une phase à partir des pièces du dossier"
            className="rounded-md border border-accent bg-accent-light px-2.5 py-1 text-[12px] text-accent-text hover:bg-[#C7D2FE] disabled:opacity-50"
          >
            {proposing ? '⏳ IA…' : '✨ Proposer (IA)'}
          </button>
        </div>
      </div>
      {state.next_hearing_date && (
        <p className="mt-2 flex items-center gap-1.5 text-[12px] text-text-secondary">
          <IconCalendarEvent size={14} stroke={1.6} />
          Prochaine audience : <strong>{formatDate(state.next_hearing_date)}</strong>
          {state.next_hearing_type ? ` (${state.next_hearing_type})` : ''}
        </p>
      )}
      {state.ai_proposed_phase && state.ai_proposed_phase !== state.current_phase && (
        <div className="mt-2 rounded-md border border-cat-imp/40 bg-cat-imp-pastel p-2 text-[12px] text-cat-imp-text">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p>
                <strong>IA propose</strong> : {PHASE_LABELS[state.ai_proposed_phase][mode]}
              </p>
              {state.ai_proposed_rationale && (
                <p className="mt-1 text-[11px] whitespace-pre-wrap text-text-secondary">
                  {state.ai_proposed_rationale}
                </p>
              )}
              {state.ai_proposed_at && (
                <p className="mt-1 text-[10px] text-text-tertiary">
                  Proposée le {new Date(state.ai_proposed_at).toLocaleString('fr-BE')}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onAcceptAi}
              className="shrink-0 rounded-md border border-cat-fav bg-cat-fav-pastel px-2 py-1 text-[11px] text-cat-fav hover:bg-cat-fav hover:text-paper-elevated"
            >
              Accepter
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detention sub-rail (parallèle aux phases)
// ---------------------------------------------------------------------------

function DetentionRail({
  state,
  mode,
  onChange,
}: {
  state: ProcedureState;
  mode: 'lawyer' | 'client';
  onChange: (patch: Partial<ProcedureState>) => void;
}) {
  const status = state.detention_status ?? 'libre';
  return (
    <div
      className={`rounded-md border p-3 text-[12.5px] ${
        status === 'detenu'
          ? 'border-cat-def/50 bg-cat-def-pastel text-cat-def'
          : 'border-rule bg-paper-elevated text-text-secondary'
      }`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <IconLock size={16} stroke={1.6} />
          <span>
            <strong>Statut :</strong> {DETENTION_LABELS[status as DetentionStatus][mode]}
          </span>
          {state.detention_start_date && (
            <span className="text-text-tertiary">
              · depuis le {formatDate(state.detention_start_date)}
            </span>
          )}
        </div>
        <select
          value={status}
          onChange={(e) => onChange({ detention_status: e.target.value as DetentionStatus })}
          className="rounded-md border border-rule-strong bg-paper-elevated px-2 py-1 text-[12.5px] text-text-primary"
        >
          <option value="libre">Libre</option>
          <option value="detenu">Détenu</option>
          <option value="libere_conditions">Libéré sous conditions</option>
          <option value="bracelet">Surveillance électronique</option>
          <option value="internement">Internement</option>
        </select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase section + events
// ---------------------------------------------------------------------------

function PhaseSection({
  phase,
  events,
  mode,
  isCurrent,
  onEdit,
  onDelete,
  onToggleChecked,
}: {
  phase: ProcedurePhase;
  events: ProcedureEvent[];
  mode: 'lawyer' | 'client';
  isCurrent: boolean;
  onEdit: (id: string) => void;
  onDelete: (ev: ProcedureEvent) => void;
  onToggleChecked: (ev: ProcedureEvent) => void;
}) {
  return (
    <div className="pl-9">
      <div className="absolute -translate-x-9 flex items-center gap-2">
        <span
          className={`flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 text-[11px] font-medium ${
            isCurrent
              ? 'border-accent bg-accent text-paper-elevated'
              : 'border-rule-strong bg-paper-elevated text-text-secondary'
          }`}
        >
          {PHASE_ORDER.indexOf(phase) + 1}
        </span>
      </div>
      <h2 className="text-[14px] font-medium text-text-primary">
        {PHASE_LABELS[phase][mode]}
        {isCurrent && (
          <span className="ml-2 rounded-pill bg-accent-light px-2 py-[1px] font-mono text-[10px] text-accent-text">
            actuelle
          </span>
        )}
      </h2>
      {events.length === 0 ? (
        <p className="mt-1 text-[12px] italic text-text-tertiary">Aucun événement enregistré.</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {events.map((ev) => (
            <EventRow
              key={ev.id}
              event={ev}
              mode={mode}
              onEdit={() => onEdit(ev.id)}
              onDelete={() => onDelete(ev)}
              onToggleChecked={() => onToggleChecked(ev)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function EventRow({
  event,
  mode,
  onEdit,
  onDelete,
  onToggleChecked,
}: {
  event: ProcedureEvent;
  mode: 'lawyer' | 'client';
  onEdit: () => void;
  onDelete: () => void;
  onToggleChecked: () => void;
}) {
  const label = eventLabel(
    event.event_type,
    mode,
    mode === 'client' ? event.label_client : event.label
  );
  // Étape future non encore atteinte = grisée et italique, badge "à venir".
  // Une fois cochée (is_checked=true), elle bascule en plein avec badge "arrivée".
  const isPendingFuture = event.is_future && !event.is_checked;
  return (
    <li
      className={`group flex items-start gap-2 rounded-md border px-3 py-2 text-[12.5px] ${
        isPendingFuture
          ? 'border-dashed border-rule bg-paper-elevated/50'
          : 'border-rule bg-paper-elevated'
      }`}
    >
      <span
        className={`mt-0.5 font-mono text-[11px] tabular-nums w-[78px] shrink-0 ${
          isPendingFuture ? 'text-text-tertiary italic' : 'text-text-tertiary'
        }`}
      >
        {formatDate(event.event_date)}
      </span>
      <IconScale
        size={14}
        stroke={1.6}
        className={`mt-0.5 shrink-0 ${
          isPendingFuture ? 'text-text-tertiary' : 'text-text-secondary'
        }`}
      />
      <div className="flex-1 min-w-0">
        <p className={isPendingFuture ? 'text-text-tertiary italic' : 'text-text-primary'}>
          {label}
          {event.is_future && !event.is_checked && (
            <span className="ml-2 rounded-pill bg-rule px-1.5 py-[1px] font-mono text-[9px] text-text-secondary">
              à venir
            </span>
          )}
          {event.is_future && event.is_checked && (
            <span className="ml-2 rounded-pill bg-cat-ok-pastel px-1.5 py-[1px] font-mono text-[9px] text-cat-ok-text">
              ✓ arrivée
            </span>
          )}
          {event.is_ai_proposed && !event.is_validated && !event.is_future && (
            <span className="ml-2 rounded-pill bg-cat-imp-pastel px-1.5 py-[1px] font-mono text-[9px] text-cat-imp-text">
              IA · à valider
            </span>
          )}
        </p>
        {event.details && <p className="mt-0.5 text-[11px] text-text-secondary">{event.details}</p>}
        {event.triggers_deadline_kind && event.deadline_date && !isPendingFuture && (
          <DeadlineBadge
            kind={event.triggers_deadline_kind}
            date={event.deadline_date}
            status={event.deadline_alert_status}
            mode={mode}
          />
        )}
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
        {event.is_future && (
          <button
            type="button"
            onClick={onToggleChecked}
            className={`rounded px-2 py-0.5 text-[11px] font-medium ${
              event.is_checked
                ? 'text-text-secondary hover:text-text-primary'
                : 'bg-accent-light text-accent-text hover:bg-accent hover:text-paper-elevated'
            }`}
            title={
              event.is_checked ? 'Re-griser (étape pas encore atteinte)' : 'Marquer comme atteinte'
            }
          >
            {event.is_checked ? '↶ Re-griser' : '✓ On y est'}
          </button>
        )}
        <button
          type="button"
          onClick={onEdit}
          className="rounded p-1 text-text-tertiary hover:text-text-primary"
          title="Modifier"
        >
          <IconEdit size={14} stroke={1.6} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded p-1 text-text-tertiary hover:text-cat-def"
          title="Supprimer"
        >
          <IconTrash size={14} stroke={1.6} />
        </button>
      </div>
    </li>
  );
}

function DeadlineBadge({
  kind,
  date,
  status,
  mode,
}: {
  kind: ProcedureEvent['triggers_deadline_kind'];
  date: string;
  status: ProcedureEvent['deadline_alert_status'];
  mode: 'lawyer' | 'client';
}) {
  if (!kind) return null;
  const styleByStatus: Record<NonNullable<typeof status>, string> = {
    ok: 'border-rule bg-paper-subtle text-text-secondary',
    soon: 'border-cat-imp/40 bg-cat-imp-pastel text-cat-imp-text',
    urgent: 'border-cat-def/50 bg-cat-def-pastel text-cat-def',
    overdue: 'border-cat-def bg-cat-def text-paper-elevated',
  };
  const cls = status ? styleByStatus[status] : styleByStatus.ok;
  return (
    <div
      className={`mt-1 inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] ${cls}`}
    >
      {status === 'urgent' || status === 'overdue' ? (
        <IconAlertTriangle size={12} stroke={1.6} />
      ) : (
        <IconCircleCheck size={12} stroke={1.6} />
      )}
      <span>
        {deadlineLabel(kind, mode)} — échéance <strong>{formatDate(date)}</strong>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Event modal (create + edit)
// ---------------------------------------------------------------------------

const EVENT_TYPE_OPTIONS: Array<{
  value: ProcedureEventType;
  label: string;
  phase: ProcedurePhase;
}> = (Object.keys(EVENT_META) as ProcedureEventType[]).map((t) => ({
  value: t,
  label: EVENT_META[t].lawyer,
  phase: EVENT_META[t].phase,
}));

function EventModal({
  dossierId,
  mode,
  role,
  existing,
  onClose,
  onSaved,
}: {
  dossierId: string;
  mode: 'lawyer' | 'client';
  role: DossierRole;
  existing?: ProcedureEvent | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [eventType, setEventType] = useState<ProcedureEventType>(existing?.event_type ?? 'plainte');
  const [eventDate, setEventDate] = useState<string>(
    existing?.event_date ?? new Date().toISOString().slice(0, 10)
  );
  const [label, setLabel] = useState(existing?.label ?? '');
  const [details, setDetails] = useState(existing?.details ?? '');
  const [saving, setSaving] = useState(false);

  const availableOptions = useMemo(() => {
    return EVENT_TYPE_OPTIONS.filter((opt) => {
      const meta = EVENT_META[opt.value];
      return meta.relevant === 'both' || meta.relevant === role;
    });
  }, [role]);

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        event_type: eventType,
        event_date: eventDate,
        label: label.trim() || null,
        details: details.trim() || null,
      };
      if (existing) {
        await api.patch(`/procedure-events/${existing.id}`, body);
      } else {
        await api.post(`/dossiers/${dossierId}/procedure/events`, body);
      }
      onSaved();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-md bg-paper-elevated p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-serif text-[16px] font-medium text-text-primary">
          {existing ? "Modifier l'événement" : 'Ajouter un événement'}
        </h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-[12px] text-text-secondary">Type d'événement</label>
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value as ProcedureEventType)}
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
            >
              {PHASE_ORDER.map((phase) => {
                const phaseOpts = availableOptions.filter((o) => o.phase === phase);
                if (phaseOpts.length === 0) return null;
                return (
                  <optgroup key={phase} label={PHASE_LABELS[phase][mode]}>
                    {phaseOpts.map((o) => (
                      <option key={o.value} value={o.value}>
                        {EVENT_META[o.value][mode]}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </div>
          <div>
            <label className="text-[12px] text-text-secondary">Date</label>
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
            />
          </div>
          <div>
            <label className="text-[12px] text-text-secondary">
              Libellé personnalisé (optionnel)
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={EVENT_META[eventType][mode]}
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
            />
          </div>
          <div>
            <label className="text-[12px] text-text-secondary">Détails / notes (optionnel)</label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
            />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Btn type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Annuler
          </Btn>
          <Btn type="button" variant="primary" onClick={submit} disabled={saving}>
            {saving ? 'Enregistrement…' : existing ? 'Mettre à jour' : 'Ajouter'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}
